import React from "react";
import { Link } from "gatsby";
import {
  FaArrowRight,
  FaBriefcase,
  FaBuilding,
  FaCloud,
  FaDownload,
  FaEdit,
  FaFileAlt,
  FaGraduationCap,
  FaHeartbeat,
  FaLandmark,
  FaLock,
  FaSearch,
  FaShieldAlt,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";

export const CAREER_PLATFORM_URL = "https://career.cloudlogicit.com.au/";
const PRODUCT_PATH = "/solutions/career-application-platform";

export const trackCareerEvent = (eventName, destination) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, {
      event_category: "career_platform",
      link_url: destination,
    });
  }
};

const appUrl = (source) =>
  `${CAREER_PLATFORM_URL}?utm_source=cloudlogic_main_site&utm_medium=referral&utm_campaign=career_platform&utm_content=${source}`;

const benefits = [
  {
    icon: <FaSearch size={22} />,
    title: "Role-aligned applications",
    text:
      "Analyse a position description and identify the skills, experience, responsibilities and requirements that matter most.",
  },
  {
    icon: <FaFileAlt size={22} />,
    title: "Professional resume creation",
    text:
      "Create a structured, readable resume designed around common ATS and recruitment review practices.",
  },
  {
    icon: <FaBriefcase size={22} />,
    title: "Tailored cover letters",
    text:
      "Generate a role-specific cover letter using the candidate's actual background, experience and supplied information.",
  },
  {
    icon: <FaEdit size={22} />,
    title: "Review before submission",
    text:
      "Review, refine and approve generated content before downloading the final resume and cover letter.",
  },
];

const outputs = [
  "Job requirement analysis",
  "Tailored resume",
  "Role-specific cover letter",
  "Application recommendations",
  "Editable review before download",
];

const steps = [
  [
    "1",
    "Add the role",
    "Paste or upload the job advertisement or position description.",
  ],
  [
    "2",
    "Add your experience",
    "Upload your current resume and include additional career information.",
  ],
  [
    "3",
    "Review your application",
    "The platform analyses the role and prepares tailored application content.",
  ],
  [
    "4",
    "Refine and download",
    "Review the content, make adjustments, and download your final documents.",
  ],
];

const industries = [
  {
    icon: <FaCloud size={22} />,
    title: "Information Technology & Cloud",
    text:
      "Useful for IT support, Microsoft 365, Azure, systems administration, infrastructure, networking, DevOps and technical support roles.",
  },
  {
    icon: <FaShieldAlt size={22} />,
    title: "Cybersecurity",
    text:
      "Helps present IAM, GRC, security operations, vulnerability management, cloud security, incident response and compliance experience clearly.",
  },
  {
    icon: <FaHeartbeat size={22} />,
    title: "Healthcare",
    text:
      "Supports healthcare IT, hospital systems, patient services, administration, personal care and operational healthcare roles.",
  },
  {
    icon: <FaUserCheck size={22} />,
    title: "Aged Care & Disability Support",
    text:
      "Can help communicate person-centred care, documentation, safety, ADLs, communication, manual handling and client support experience.",
  },
  {
    icon: <FaBuilding size={22} />,
    title: "Professional & Business Services",
    text:
      "Relevant for administration, customer service, business support, operations, project coordination, finance support and HR support.",
  },
  {
    icon: <FaLandmark size={22} />,
    title: "Government & Public Sector",
    text:
      "Helps candidates structure evidence around capability statements, service delivery, documentation, compliance and stakeholder management.",
  },
  {
    icon: <FaLock size={22} />,
    title: "Financial Services",
    text:
      "Useful for banking, insurance, superannuation, FinTech, payments, financial operations, technology and cybersecurity roles.",
  },
  {
    icon: <FaGraduationCap size={22} />,
    title: "Education",
    text:
      "Supports applications across schools, universities, training providers, education technology, administration and IT support.",
  },
];

const audiences = [
  "Professionals applying for a new role",
  "Candidates changing industries",
  "Graduates entering the workforce",
  "Experienced professionals targeting specialised roles",
  "Contractors applying for project-based opportunities",
  "Professionals returning to the workforce",
  "Candidates who need to tailor an existing resume quickly",
];

const differentiators = [
  [
    "Built around the actual role",
    "The platform starts with employer requirements rather than simply rewriting an existing resume.",
  ],
  [
    "Your information remains the source",
    "Generated documents should be based on the experience, education, skills and achievements you provide.",
  ],
  [
    "See what employers are asking for",
    "Important responsibilities, skills and requirements are summarised before the application is finalised.",
  ],
  [
    "Review everything before download",
    "Users remain in control of the final application and should verify all content before submission.",
  ],
];

const faqs = [
  [
    "Does this guarantee an interview or job?",
    "No. The platform helps create role-focused application documents, but it does not guarantee interviews, selection or employment outcomes.",
  ],
  [
    "Does the platform make recruitment decisions?",
    "No. It helps candidates prepare application documents. Employers and recruiters make their own decisions.",
  ],
  [
    "Can I edit the generated resume and cover letter?",
    "Yes. Users should review and refine generated content before downloading and submitting final documents.",
  ],
  [
    "Where do existing users log in?",
    "Existing users can continue through the CloudLogic Career AI application subdomain.",
  ],
];

export const CareerPlatformTeaser = () => {
  const startUrl = appUrl("homepage_teaser");

  return (
    <section className="career-teaser-area">
      <div className="container">
        <div className="career-teaser-panel">
          <div className="career-teaser-copy">
            <span className="sub-title">CloudLogic Digital Solution</span>
            <h2>Create Stronger, Role-Focused Job Applications</h2>
            <p>
              CloudLogic Career AI helps professionals turn their existing
              experience into a tailored resume and cover letter aligned with
              the requirements of a specific role.
            </p>

            <div className="career-teaser-benefits">
              {benefits.map((benefit) => (
                <div key={benefit.title}>
                  {benefit.icon}
                  <span>{benefit.title}</span>
                </div>
              ))}
            </div>

            <div className="career-actions">
              <a
                href={startUrl}
                className="default-btn"
                onClick={() =>
                  trackCareerEvent("career_platform_home_start_click", startUrl)
                }
              >
                Create Your Application
              </a>
              <Link
                to={PRODUCT_PATH}
                className="career-secondary-link"
                onClick={() =>
                  trackCareerEvent(
                    "career_platform_home_learn_click",
                    PRODUCT_PATH
                  )
                }
              >
                Learn How It Works <FaArrowRight size={13} />
              </Link>
            </div>
          </div>

          <div
            className="career-teaser-card"
            aria-label="CloudLogic Career AI outputs"
          >
            <span>What users receive</span>
            <h3>Resume, Cover Letter and Role Guidance</h3>
            <ul>
              {outputs.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <small>
              No guaranteed-job claims. Users review before download.
            </small>
          </div>
        </div>
      </div>
    </section>
  );
};

const CareerPlatformContent = () => {
  const heroUrl = appUrl("product_hero");
  const processUrl = appUrl("how_it_works");
  const loginUrl = appUrl("existing_user_login");

  React.useEffect(() => {
    trackCareerEvent("career_platform_page_view", PRODUCT_PATH);
  }, []);

  return (
    <main className="career-platform-page">
      <section className="career-product-hero">
        <div className="container">
          <div className="career-product-layout">
            <div className="career-product-copy">
              <span className="career-product-badge">CloudLogic Career AI</span>
              <h1>Turn Your Experience Into a Stronger Job Application</h1>
              <p>
                CloudLogic's AI-powered career application platform analyses
                your target role alongside your professional background to help
                create a tailored resume and cover letter that clearly
                communicate your most relevant experience.
              </p>
              <div className="career-actions">
                <a
                  href={heroUrl}
                  className="default-btn"
                  onClick={() =>
                    trackCareerEvent(
                      "career_platform_start_application_click",
                      heroUrl
                    )
                  }
                >
                  Start Your Application
                </a>
                <a
                  href="#how-it-works"
                  className="career-secondary-link"
                  onClick={() =>
                    trackCareerEvent(
                      "career_platform_see_how_it_works_click",
                      "#how-it-works"
                    )
                  }
                >
                  See How It Works <FaArrowRight size={13} />
                </a>
              </div>
              <div className="career-hero-assurances">
                <span>Tailored to the target role</span>
                <span>Review before download</span>
                <span>Existing users can continue in the app</span>
              </div>
            </div>

            <aside className="career-product-card">
              <span>Application workflow</span>
              <h2>From role requirements to reviewable documents</h2>
              <ul>
                <li>
                  <FaSearch /> Role analysis
                </li>
                <li>
                  <FaFileAlt /> Tailored resume
                </li>
                <li>
                  <FaBriefcase /> Cover letter
                </li>
                <li>
                  <FaDownload /> Download-ready files
                </li>
              </ul>
              <a
                href={loginUrl}
                onClick={() =>
                  trackCareerEvent(
                    "career_platform_existing_user_login_click",
                    loginUrl
                  )
                }
              >
                Existing User Login <FaArrowRight size={13} />
              </a>
            </aside>
          </div>
        </div>
      </section>

      <section className="career-section">
        <div className="container">
          <div className="section-title">
            <span className="sub-title">What You Get</span>
            <h2>Clear Outputs Before You Submit Anything</h2>
            <p>
              The platform helps candidates understand the role, prepare
              professional documents and review the final application before
              using it with employers or recruiters.
            </p>
          </div>
          <div className="career-benefit-grid">
            {benefits.map((benefit) => (
              <article className="career-benefit-card" key={benefit.title}>
                <div>{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className="career-section career-process-section"
        id="how-it-works"
      >
        <div className="container">
          <div className="section-title">
            <span className="sub-title">How It Works</span>
            <h2>Four Simple Steps. You Stay in Control.</h2>
          </div>
          <div className="career-step-grid">
            {steps.map(([number, title, text]) => (
              <article className="career-step-card" key={number}>
                <strong>{number}</strong>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
          <div className="career-centered-cta">
            <a
              href={processUrl}
              className="default-btn"
              onClick={() =>
                trackCareerEvent(
                  "career_platform_process_start_click",
                  processUrl
                )
              }
            >
              Start Now
            </a>
          </div>
        </div>
      </section>

      <section className="career-section">
        <div className="container">
          <div className="section-title">
            <span className="sub-title">Who It Is For</span>
            <h2>
              For Candidates Who Need a Clearer, More Relevant Application
            </h2>
          </div>
          <div className="career-audience-list">
            {audiences.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="career-section career-industries-section">
        <div className="container">
          <div className="section-title">
            <span className="sub-title">Industries & Expertise</span>
            <h2>Useful Across Technical, Care, Business and Regulated Roles</h2>
            <p>
              The platform does not provide formal recruitment endorsement. It
              helps candidates present supplied experience using clearer,
              role-relevant language for the industries they are targeting.
            </p>
          </div>
          <div className="career-industry-grid">
            {industries.map((industry) => (
              <article className="career-industry-card" key={industry.title}>
                <div>{industry.icon}</div>
                <h3>{industry.title}</h3>
                <p>{industry.text}</p>
                <a
                  href={appUrl(
                    `industry_${industry.title
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "_")}`
                  )}
                  onClick={() =>
                    trackCareerEvent(
                      "career_platform_industry_cta_click",
                      appUrl(
                        `industry_${industry.title
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "_")}`
                      )
                    )
                  }
                >
                  Create Application <FaArrowRight size={13} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="career-section">
        <div className="container">
          <div className="career-difference-panel">
            <div>
              <span className="sub-title">Why CloudLogic Career AI</span>
              <h2>More Than a Generic Resume Generator</h2>
              <p>
                CloudLogic Career AI is positioned as a practical digital
                solution: role-first, evidence-aware, reviewable and connected
                to CloudLogic's broader technology delivery capability.
              </p>
            </div>
            <div className="career-difference-grid">
              {differentiators.map(([title, text]) => (
                <article key={title}>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="career-section career-trust-section">
        <div className="container">
          <div className="career-trust-layout">
            <div>
              <span className="sub-title">Trust & Transparency</span>
              <h2>Your Career Information Deserves Careful Handling</h2>
              <p>
                Uploaded documents are used to prepare the requested
                application. Users should review all generated information
                before submitting it. The system should not intentionally invent
                employment history or qualifications, and users remain
                responsible for verifying final application content.
              </p>
            </div>
            <ul>
              <li>
                The platform does not guarantee interviews or employment
                outcomes.
              </li>
              <li>
                Generated information should be checked for accuracy before use.
              </li>
              <li>Admin access is not promoted through customer navigation.</li>
              <li>
                <a href={`${CAREER_PLATFORM_URL}privacy`}>Privacy Policy</a>,{" "}
                <a href={`${CAREER_PLATFORM_URL}terms`}>Terms of Use</a>,{" "}
                <a href={`${CAREER_PLATFORM_URL}support`}>Support</a> and{" "}
                <Link to="/contact">Contact CloudLogic</Link> are available.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="career-section">
        <div className="container">
          <div className="career-organisation-cta">
            <div>
              <FaUsers size={28} />
              <span className="sub-title">
                Organisations & Workforce Programs
              </span>
              <h2>Interested in Using the Platform for Your Organisation?</h2>
              <p>
                CloudLogic can discuss access for education providers, workforce
                programs, community organisations, career services, training
                organisations and businesses supporting candidates at scale.
              </p>
            </div>
            <Link
              to="/contact"
              className="default-btn"
              onClick={() =>
                trackCareerEvent(
                  "career_platform_organisation_enquiry_click",
                  "/contact"
                )
              }
            >
              Talk to CloudLogic
            </Link>
          </div>
        </div>
      </section>

      <section className="career-section career-faq-section">
        <div className="container">
          <div className="section-title">
            <span className="sub-title">Questions</span>
            <h2>Clear Answers, No Exaggerated Claims</h2>
          </div>
          <div className="career-faq-list">
            {faqs.map(([question, answer]) => (
              <details
                key={question}
                onToggle={(event) => {
                  if (event.currentTarget.open) {
                    trackCareerEvent("career_platform_faq_open", question);
                  }
                }}
              >
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default CareerPlatformContent;
